import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { toast } from 'react-toastify';
import EventBus from "../common/EventBus";
import { Table, Pagination, Modal, Button } from 'react-bootstrap';
import reportService from "../services/report.service";
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

const ClientShopWorkstationReport = () => {
    const { user: currentUser } = useSelector((state) => state.auth);

    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const [page, setPage] = useState(0);
    const [size] = useState(50);
    const [totalPages, setTotalPages] = useState(0);
    const [showUnblockAfterLastMapModal, setShowUnblockAfterLastMapModal] = useState(false);
    const [workstationToManage, setWorkstationToManage] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalData, setConfirmModalData] = useState({ title: '', body: '', onConfirm: () => {} });

    // W/B List Modal state
    const [showWbModal, setShowWbModal] = useState(false);
    const [wbModalClientName, setWbModalClientName] = useState("");
    const [wbWhiteList, setWbWhiteList] = useState([]);
    const [wbBlackList, setWbBlackList] = useState([]);
    const [wbModalLoading, setWbModalLoading] = useState(false);

    const handleShowWbList = (clientId, clientName) => {
        setWbModalClientName(clientName);
        setShowWbModal(true);
        setWbModalLoading(true);
        setWbWhiteList([]);
        setWbBlackList([]);
        
        reportService.getClientWbList(clientId).then(
            (response) => {
                setWbWhiteList(response.data.whiteList || []);
                setWbBlackList(response.data.blackList || []);
                setWbModalLoading(false);
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to load W/B list." });
                setWbModalLoading(false);
            }
        );
    };

    // Sorting states
    const [orderBy, setOrderBy] = useState("shopName");
    const [sortDirection, setSortDirection] = useState("ASC");

    const handleSort = (field) => {
        if (orderBy === field) {
            setSortDirection(prev => prev === "ASC" ? "DESC" : "ASC");
        } else {
            setOrderBy(field);
            setSortDirection("ASC");
        }
    };

    const renderSortIcon = (field) => {
        if (orderBy !== field) {
            return <FaSort className="ms-1 text-white-50" style={{ opacity: 0.6 }} />;
        }
        return sortDirection === "ASC" 
            ? <FaSortUp className="ms-1 text-warning" />
            : <FaSortDown className="ms-1 text-warning" />;
    };

    const copyTextToClipboard = async (value, label) => {
        if (!value) return;

        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(value);
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = value;
                textArea.setAttribute("readonly", "");
                textArea.style.position = "fixed";
                textArea.style.left = "-9999px";
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
            }

            toast.success(`${label} copied to clipboard`);
        } catch (error) {
            console.error("Failed to copy to clipboard:", error);
            toast.error(`Failed to copy ${label}`);
        }
    };

    const renderCopyButton = (value, label) => (
        <button
            type="button"
            className="btn btn-sm border-0 bg-transparent text-secondary p-0"
            onClick={(e) => {
                e.stopPropagation();
                copyTextToClipboard(value, label);
            }}
            disabled={!value}
            title={`Copy ${label}`}
            aria-label={`Copy ${label}`}
            style={{ boxShadow: "none", lineHeight: 1 }}
        >
            <i className="bi bi-copy"></i>
        </button>
    );

    const getSortedReportData = () => {
        if (!reportData) return [];
        return [...reportData].sort((a, b) => {
            const valA = a[orderBy] || "";
            const valB = b[orderBy] || "";
            
            const strA = String(valA).trim().toLowerCase();
            const strB = String(valB).trim().toLowerCase();
            
            if (strA < strB) return sortDirection === "ASC" ? -1 : 1;
            if (strA > strB) return sortDirection === "ASC" ? 1 : -1;
            return 0;
        });
    };

    useEffect(() => {
        if (currentUser && currentUser.roles.includes("ROLE_ADMIN")) {
            fetchReportData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, currentUser]);

    const fetchReportData = () => {
        setLoading(true);
        reportService.getClientShopWorkstations(null, page, size).then(
            (response) => {
                setReportData(response.data.content);
                setTotalPages(response.data.totalPages);
                setLoading(false);
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to load report data." });
                setLoading(false);
            }
        );
    };





    const handleWhitelist = (shopName, index, span) => {
        const group = reportData.slice(index, index + span);
        const clientIds = [...new Set(group.map(r => r.clientId))];

        setConfirmModalData({
            title: 'White List Shop',
            body: `Do you want to whitelist "${shopName}" for the ${clientIds.length} unique client(s) in this group? This will clear all matching reports.`,
            onConfirm: () => {
                setActionLoading(true);
                reportService.whitelistShop(shopName, clientIds).then(
                    () => {
                        toast.success("Shop whitelisted successfully.");
                        setReportData(prevData => prevData.filter(row => row.shopName !== shopName));
                        setActionLoading(false);
                        setShowConfirmModal(false);
                    },
                    (error) => {
                        EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to whitelist shop." });
                        setActionLoading(false);
                    }
                );
            }
        });
        setShowConfirmModal(true);
    };

    const handleBlacklist = (shopName) => {
        setConfirmModalData({
            title: 'Black List Shop',
            body: `Do you want to blacklist "${shopName}"? This will permanently block this shop name and clear all matching reports.`,
            onConfirm: () => {
                setActionLoading(true);
                reportService.blacklistShop(shopName).then(
                    () => {
                        toast.success("Shop blacklisted successfully.");
                        setReportData(prevData => prevData.filter(row => row.shopName !== shopName));
                        setActionLoading(false);
                        setShowConfirmModal(false);
                    },
                    (error) => {
                        EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to blacklist shop." });
                        setActionLoading(false);
                    }
                );
            }
        });
        setShowConfirmModal(true);
    };

    const handleDeleteReports = (shopName) => {
        setConfirmModalData({
            title: 'Delete Shop Reports',
            body: `Are you sure you want to delete all reports for "${shopName}"?`,
            onConfirm: () => {
                setActionLoading(true);
                reportService.deleteShopReportsByShopName(shopName).then(
                    () => {
                        toast.success("Shop reports deleted successfully.");
                        setReportData(prevData => prevData.filter(row => row.shopName !== shopName));
                        setActionLoading(false);
                        setShowConfirmModal(false);
                    },
                    (error) => {
                        EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to delete shop reports." });
                        setActionLoading(false);
                    }
                );
            }
        });
        setShowConfirmModal(true);
    };





    const confirmUnblockAfterLastMap = () => {
        if (!workstationToManage) return;
        
        setActionLoading(true);
        reportService.unblockWorkstation(workstationToManage.workstationId).then(
            () => {
                toast.success("Workstation unblocked successfully.");
                setShowUnblockAfterLastMapModal(false);
                setWorkstationToManage(null);
                setActionLoading(false);
                fetchReportData();
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to unblock workstation." });
                setActionLoading(false);
            }
        );
    };



    if (!currentUser) return <Navigate to="/login" />;
    if (!currentUser.roles.includes("ROLE_ADMIN")) {
        return (
            <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
                <header className="jumbotron">
                    <h3><strong>Unauthorized:</strong> You must be an administrator to access this report.</h3>
                </header>
            </div>
        );
    }

    const getRowSpans = (data) => {
        const spans = [];
        let i = 0;
        while (i < data.length) {
            let j = i + 1;
            while (j < data.length && data[j].shopName === data[i].shopName) {
                j++;
            }
            spans[i] = j - i;
            for (let k = i + 1; k < j; k++) {
                spans[k] = 0;
            }
            i = j;
        }
        return spans;
    };

    const sortedData = getSortedReportData();
    const rowSpans = getRowSpans(sortedData);

    return (
        <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
            <style>{`
                .sortable-header {
                    transition: color 0.15s ease-in-out;
                }
                .sortable-header:hover {
                    color: #ffc107 !important; /* bootstrap warning yellow */
                }
            `}</style>
            {loading && !reportData.length && (
                <div className="text-center mb-3">
                    <span className="spinner-border text-primary" role="status"></span>
                </div>
            )}


                            


                            {/* Table */}
            <div className="card shadow-sm border-0">
                <div className="card-body p-0">
                    <Table responsive hover className="mb-0 align-middle table-bordered">
                        <thead className="table-dark">
                            <tr>
                                <th 
                                    onClick={() => handleSort("shopName")} 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    className="border-0 sortable-header"
                                >
                                    Shop Name / Actions {renderSortIcon("shopName")}
                                </th>
                                <th 
                                    onClick={() => handleSort("clientName")} 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    className="border-0 sortable-header"
                                >
                                    Client Name {renderSortIcon("clientName")}
                                </th>
                                <th className="border-0">WS ID</th>
                                <th className="border-0">Workstation Name</th>
                                <th className="border-0">Recent Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedData.length > 0 ? (
                                sortedData.map((row, index) => {
                                    const span = rowSpans[index];
                                    const dateStr = row.recentDate ? new Date(row.recentDate).toLocaleString() : <span className="text-muted italic">Unknown</span>;
                                    
                                    return (
                                        <tr key={row.id} style={{ 
                                            backgroundColor: '#ffffff',
                                            borderTop: span > 0 && index > 0 ? '6px solid #dee2e6' : '1px solid #dee2e6'
                                        }}>
                                            {span > 0 && (
                                                <td rowSpan={span} className="bg-white align-top pt-3" style={{ minWidth: '220px' }}>
                                                    <div className="fw-bold mb-1 d-flex align-items-center gap-1">
                                                        <span>{row.shopName}</span>
                                                        {renderCopyButton(row.shopName, "Shop Name")}
                                                    </div>
                                                    {row.shopAddress && (
                                                        <div className="text-muted small mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                                                            {row.shopAddress}
                                                        </div>
                                                    )}
                                                    <div className="d-flex flex-column gap-2">
                                                        <Button variant="primary" size="sm" onClick={() => handleWhitelist(row.shopName, index, span)}>White List</Button>
                                                        <Button variant="dark" size="sm" onClick={() => handleBlacklist(row.shopName)}>Black List</Button>
                                                        <Button variant="outline-danger" size="sm" onClick={() => handleDeleteReports(row.shopName)}>Delete</Button>
                                                    </div>
                                                </td>
                                            )}
                                            <td>
                                                <div className="d-flex align-items-center gap-1">
                                                    <div
                                                        className="fw-bold text-primary"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleShowWbList(row.clientId, row.clientName);
                                                        }}
                                                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                        title="Show W/B list"
                                                    >
                                                        {row.clientName}
                                                    </div>
                                                    {renderCopyButton(row.clientName, "Client Name")}
                                                </div>
                                                {row.clientAddress && (
                                                    <div className="text-muted small mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                                                        {row.clientAddress}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="text-secondary small">
                                                {row.workstationId}
                                            </td>
                                            <td>
                                                <div className="fw-bold d-flex align-items-center gap-1">
                                                    <span>{row.workstationName}</span>
                                                    {renderCopyButton(row.workstationName, "Workstation Name")}
                                                </div>
                                                {row.shopAddress && (
                                                    <div className="text-muted small mt-1">
                                                        {row.shopAddress}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="small text-muted">
                                                {dateStr}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-4 text-muted">
                                        {loading ? "Loading..." : "No workstation reports found."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <Pagination>
                        <Pagination.First onClick={() => setPage(0)} disabled={page === 0} />
                        <Pagination.Prev onClick={() => setPage(page - 1)} disabled={page === 0} />
                        {[...Array(totalPages)].map((_, i) => {
                            if (i < page - 2 || i > page + 2) return null;
                            return (
                                <Pagination.Item
                                    key={i}
                                    active={i === page}
                                    onClick={() => setPage(i)}
                                >
                                    {i + 1}
                                </Pagination.Item>
                            );
                        })}
                        <Pagination.Next onClick={() => setPage(page + 1)} disabled={page === totalPages - 1} />
                        <Pagination.Last onClick={() => setPage(totalPages - 1)} disabled={page === totalPages - 1} />
                    </Pagination>
                </div>
            )}

            {/* Modals */}




            <Modal show={showUnblockAfterLastMapModal} onHide={() => setShowUnblockAfterLastMapModal(false)} centered backdrop="static">
                <Modal.Header>
                    <Modal.Title className="text-primary">All Violations Cleared</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    This was the last remaining report for <strong>{workstationToManage?.workstationName}</strong>. 
                    The workstation is currently blocked. Would you like to <strong>Unblock</strong> it now?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => { setShowUnblockAfterLastMapModal(false); setWorkstationToManage(null); }}>No, Keep Blocked</Button>
                    <Button variant="success" onClick={confirmUnblockAfterLastMap} disabled={actionLoading}>
                        {actionLoading ? "Unblocking..." : "Yes, Unblock Now"}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{confirmModalData.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {confirmModalData.body}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={confirmModalData.onConfirm} disabled={actionLoading}>
                        {actionLoading ? "Processing..." : "Confirm"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* W/B List Modal */}
            <Modal show={showWbModal} onHide={() => setShowWbModal(false)} centered size="lg">
                <Modal.Header closeButton className="bg-dark text-white py-3">
                    <Modal.Title className="fw-semibold">
                        Shop White / Black List - {wbModalClientName}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {wbModalLoading ? (
                        <div className="text-center py-5">
                            <span className="spinner-border text-primary me-2" role="status"></span>
                            <span className="text-muted fw-semibold fs-5">Loading lists...</span>
                        </div>
                    ) : (
                        <div className="row g-4">
                            {/* Whitelisted Shops */}
                            <div className="col-md-6 border-end">
                                <h5 className="text-success border-bottom pb-2 mb-3 fw-bold d-flex justify-content-between align-items-center">
                                    <span>White Listed Shops</span>
                                    <span className="badge bg-success rounded-pill fs-6">{wbWhiteList.length}</span>
                                </h5>
                                {wbWhiteList.length > 0 ? (
                                    <div className="list-group" style={{ maxHeight: '40vh', overflowY: 'auto' }}>
                                        {wbWhiteList.map((shop, i) => (
                                            <div key={i} className="list-group-item list-group-item-success py-2 px-3 fw-semibold">
                                                {shop}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="alert alert-light border text-muted py-3 px-3">
                                        No whitelisted shop names recorded for this client.
                                    </div>
                                )}
                            </div>

                            {/* Blacklisted Shops */}
                            <div className="col-md-6">
                                <h5 className="text-danger border-bottom pb-2 mb-3 fw-bold d-flex justify-content-between align-items-center">
                                    <span>Black Listed Shops</span>
                                    <span className="badge bg-danger rounded-pill fs-6">{wbBlackList.length}</span>
                                </h5>
                                {wbBlackList.length > 0 ? (
                                    <div className="list-group" style={{ maxHeight: '40vh', overflowY: 'auto' }}>
                                        {wbBlackList.map((shop, i) => (
                                            <div key={i} className="list-group-item list-group-item-danger py-2 px-3 fw-semibold">
                                                {shop}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="alert alert-light border text-muted py-3 px-3">
                                        No blacklisted shop names recorded.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowWbModal(false)} className="fw-semibold px-4">
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ClientShopWorkstationReport;
